import React, {useState, useEffect} from 'react'
import {useSelectedVenue} from '../../contexts/SelectedVenueContext'
// This tree shakes, it's why we use lodash-es instead of lodash
import * as _ from 'lodash-es'
import {debounce} from 'lodash-es'
import {fetchVenuesServiceUtil, NetworkError} from '../../services'
import {
  getIsNumberOrStringRepresentationOfNumber,
  castStringToNumber,
  convertCountryAlpha2CodeToAlpha3,
} from '../../utils'
import {useToast} from '../../contexts/ToastContext'
import {FetchVenuesResponse as Address} from '../../services'
import {getCurrentLocation, type GeoLocationErrorType} from '../../utils/geolocation'

import ClearableFieldWrapper from '../../components/ClearableFieldWrapper/ClearableFieldWrapper'
import GenericModal from '../../components/GenericModal/GenericModal'
import InlineToast from '../../components/InlineToast/InlineToast'
import LocationPermissionsAccordion from '../../components/LocationPermissionsAccordion/LocationPermissionsAccordion'
import LoadingSpinner from '../../images/svg/LoadingSpinner'
import OrDivider from '../../components/OrDivider/OrDivider'
import VenueSubmissionForm from './VenueSubmissionForm/VenueSubmissionForm'

const buttonHelperTextStyles = 'text-xs text-gray-500 dark:text-slate-400 text-center mt-1'
const inlineToastContainerMainStyles = 'flex justify-center items-center py-7'
const inlineToastContainerMaxWidthStyles = 'max-w-[300px]'
type VenueSearchFailure = 'network' | 'server'

const venueSearchErrorCopy: Record<
  VenueSearchFailure,
  {title: string; message: string; variant: 'warning' | 'error'}
> = {
  network: {
    title: 'Connection Problem',
    message: "We couldn't reach the server. Check your internet connection and try again.",
    variant: 'warning',
  },
  server: {
    title: 'Error Retrieving Locations',
    message:
      'There was an error retrieving locations. Please try again. If the problem persists, refresh the page or contact support.',
    variant: 'error',
  },
}

const permissionToastTitle = 'Location Access Needed'
const permissionToastDescription =
  'Location access is blocked, to find venues near you, please ensure that location access is enabled for your device, browser, and this website. Review the steps below, then try again.'

type LocationReadError = Extract<
  GeoLocationErrorType,
  'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'UNKNOWN'
>

const locationReadErrorCopy: Record<LocationReadError, {title: string; message: string}> = {
  POSITION_UNAVAILABLE: {
    title: 'Location Unavailable',
    message: 'Your location could not be determined. Please try again.',
  },
  TIMEOUT: {
    title: 'Location Request Timed Out',
    message: 'Finding your location took too long. Please try again.',
  },
  UNKNOWN: {
    title: 'Unable to Read Location',
    message: 'Something went wrong while reading your location. Please try again.',
  },
}

export default function ChooseLocationPage({
  setChosenLocation,
}: {
  setChosenLocation: (address: Address) => void
}) {
  const {setSelectedVenue} = useSelectedVenue()
  const isGeolocationSupportedByThisBrowser = Boolean(navigator.geolocation)
  // codeCommentsGeolocationPermissionChange. The possible values are "granted,"
  // "prompt" and "denied."
  const [geolocationPermissionState, setGeolocationPermissionState] = useState<PermissionState>(
    // It doesn't matter what we set this initial value to because it will be
    // immediately overwriten by a useEffect function below that runs on
    // component mount.
    'prompt'
  )

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [venueSearchFailure, setVenueSearchFailure] = useState<VenueSearchFailure | null>(null)
  const [hasFetchSucceededAtLeastOnce, setHasFetchSucceededAtLeastOnce] = useState<boolean>(false)
  const [nearbyLocationsResults, setNearbyLocationsResults] = useState<Address[]>([])
  const [addressSearchInput, setAddressSearchInput] = useState<string>('')
  const [addressSearchResults, setAddressSearchResults] = useState<Address[]>([])
  const [
    showNearbyLocationResultsRatherThanSearchInputResults,
    setShowNearbyLocationResultsRatherThanSearchInputResults,
  ] = useState<boolean>(false)
  const [showNoResultsFoundMessage, setShowNoResultsFoundMessage] = useState<boolean>(false)
  const [locationReadError, setLocationReadError] = useState<LocationReadError | null>(null)

  const [toggleVenueSubmissionForm, setToggleVenueSubmissionForm] = useState<boolean>(false)
  const [venueSubmissionStatus, setVenueSubmissionStatus] = useState<
    'idle' | 'submitting' | 'submitted' | 'error'
  >('idle')
  const {showToast} = useToast()

  useEffect(() => {
    if (venueSubmissionStatus === 'submitted') {
      showToast({
        title: 'Venue Submitted Successfully',
        description: 'Thank you for submitting your venue details.',
        variant: 'success',
        overlayMode: 'dim',
        autoDismiss: false,
      })
      setVenueSubmissionStatus('idle')
    }
    if (venueSubmissionStatus === 'error') {
      showToast({
        title: 'Error Submitting Venue',
        description:
          'There was an error submitting your venue. Please try again, if the problem persists, refresh the page or contact support.',
        variant: 'error',
        overlayMode: 'dim',
        autoDismiss: false,
      })
      setVenueSubmissionStatus('idle')
    }
  }, [venueSubmissionStatus, showToast])

  // codeCommentsGeolocationPermissionChange
  useEffect(() => {
    navigator.permissions
      .query({name: 'geolocation'})
      .then((permissionStatus: PermissionStatus): void => {
        setGeolocationPermissionState(permissionStatus.state)
        permissionStatus.onchange = (event: any) => {
          setGeolocationPermissionState(event.currentTarget.state)
        }
      })
  }, [])

  const fetchVenues = React.useCallback(
    async (searchQuery: string, isUserGeocodingSearch = false) => {
      setVenueSearchFailure(null)
      setHasFetchSucceededAtLeastOnce(false)
      setIsFetching(true)
      setShowNoResultsFoundMessage(false)

      try {
        const venuesResponse = await fetchVenuesServiceUtil(searchQuery)
        const dedupedAddresses = dedupeAddresses(venuesResponse)
        if (isUserGeocodingSearch) {
          setNearbyLocationsResults(dedupedAddresses)
          setShowNearbyLocationResultsRatherThanSearchInputResults(true)
        } else {
          setAddressSearchResults(
            dedupedAddresses.sort((a: Address, b: Address) => a.name.localeCompare(b.name))
          )
          if (dedupedAddresses.length === 0 && addressSearchInput.length > 0) {
            setShowNoResultsFoundMessage(true)
          } else {
            setShowNoResultsFoundMessage(false)
          }
        }
        setHasFetchSucceededAtLeastOnce(true)
      } catch (error) {
        console.error(error)
        setVenueSearchFailure(error instanceof NetworkError ? 'network' : 'server')
      } finally {
        setIsFetching(false)
      }
    },
    [addressSearchInput.length]
  )

  const debouncedFetchVenues = React.useMemo(() => debounce(fetchVenues, 300), [fetchVenues])

  useEffect(() => {
    if (addressSearchInput.length === 0) {
      setShowNoResultsFoundMessage(false)
    }
  }, [addressSearchInput])

  useEffect(() => {
    if (!addressSearchInput) {
      setAddressSearchResults([])
      return
    }
    debouncedFetchVenues(`byString?${new URLSearchParams({searchString: addressSearchInput})}`)
    return () => {
      debouncedFetchVenues.cancel()
    }
  }, [addressSearchInput, debouncedFetchVenues])

  const resultsListToDisplay = showNearbyLocationResultsRatherThanSearchInputResults
    ? nearbyLocationsResults
    : addressSearchResults

  const handleSubmitVenueOnClick = () => setToggleVenueSubmissionForm(true)

  const showLocationPermissionDeniedToast = () => {
    setGeolocationPermissionState('denied')
    showToast({
      title: permissionToastTitle,
      description: permissionToastDescription,
      variant: 'warning',
      autoDismiss: false,
      overlayMode: 'dim',
      children: <LocationPermissionsAccordion />,
    })
  }

  const handleUseCurrentLocationOnClick = async () => {
    if (!isGeolocationSupportedByThisBrowser) return

    // If already denied → guide user to enable location access, don't try to request location access
    // again because it won't work and will just lead to a worse user experience.
    if (geolocationPermissionState === 'denied') {
      setLocationReadError(null)
      showLocationPermissionDeniedToast()
      return
    }

    setLocationReadError(null)
    setVenueSearchFailure(null)
    setIsFetching(true)
    setAddressSearchInput('')

    const result = await getCurrentLocation()

    if (!result.success) {
      setIsFetching(false)

      if (result.error === 'PERMISSION_DENIED') {
        showLocationPermissionDeniedToast()
        return
      }

      if (result.error === 'NOT_SUPPORTED') {
        return
      }

      setLocationReadError(result.error)
      return
    }

    const {latitude, longitude, accuracy, accuracyStatus} = result
    setGeolocationPermissionState('granted')

    if (accuracyStatus === 'LOW' || accuracyStatus === 'POOR') {
      showToast({
        title: 'Low Location Accuracy',
        description: `Your location is only accurate to about ${Math.round(accuracy)} metres. Please try again for a more precise reading.`,
        variant: 'warning',
        autoDismiss: false,
        overlayMode: 'dim',
      })
    }

    const searchQuery = `nearby?${new URLSearchParams({
      lat: latitude.toString(),
      long: longitude.toString(),
      maxDistanceKm: '50',
    })}`

    await fetchVenues(searchQuery, true)
  }

  const handleResultClick = (address: any) => {
    setChosenLocation(address)
    setSelectedVenue(address)
  }

  const renderedResultsList = () => {
    const results = resultsListToDisplay.map((address: Address) => (
      <li
        // Remove top/bottom padding when first/last child
        className={[
          'flex flex-row justify-between py-4',
          'hover:cursor-pointer hover:bg-neutral-50 dark:hover:bg-slate-800/60',
        ].join(' ')}
        role="option"
        aria-selected={false}
        key={address.addressId}
        onClick={() => handleResultClick(address)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleResultClick(address)
          }
        }}
      >
        <div className="ml-3 overflow-hidden">
          {showNearbyLocationResultsRatherThanSearchInputResults &&
            getIsNumberOrStringRepresentationOfNumber(address.distanceKm) && (
              <p className=" text-xs text-sky-500 dark:text-sky-400 pr-1">
                {/* @ts-ignore */}
                {formatAddressDistanceForDisplay(address.distanceKm)} km
              </p>
            )}
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
            <span>{address.name}</span>
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{address.address1}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
            {`${address.city}, ${address.stateOrProvince && address.stateOrProvince !== address.city ? address.stateOrProvince : ''} ${convertCountryAlpha2CodeToAlpha3(address.country)} ${address.postalCode}`}
          </p>
        </div>
        <div className="flex flex-col justify-center align-middle">
          <svg
            className="w-6 h-auto text-slate-500 dark:text-slate-400"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
              d="m10 16 4-4-4-4"
            />
          </svg>
        </div>
      </li>
    ))
    return results
  }

  return (
    <>
      <GenericModal
        isOpen={toggleVenueSubmissionForm}
        onClose={() => setToggleVenueSubmissionForm(false)}
        title="Venue Submission Form"
        closeOnBackdrop={venueSubmissionStatus !== 'submitting'}
      >
        <VenueSubmissionForm
          setToggleVenueSubmissionForm={setToggleVenueSubmissionForm}
          venueSubmissionStatus={venueSubmissionStatus}
          setVenueSubmissionStatus={setVenueSubmissionStatus}
        />
      </GenericModal>
      <div className="flex items-center flex-col">
        <button
          id="clp_useCurrentLocationButton"
          className={[
            isGeolocationSupportedByThisBrowser
              ? 'bg-sky-500 hover:bg-sky-400 focus:bg-sky-400 dark:bg-sky-600 dark:hover:bg-sky-500 dark:focus:bg-sky-500'
              : 'bg-gray-300 dark:bg-slate-700',
            'text-white font-semibold py-1.5 px-5 rounded inline-flex items-center focus:outline-none',
            'flex gap-2 items-center',
          ].join(' ')}
          disabled={!isGeolocationSupportedByThisBrowser || isFetching}
          onClick={handleUseCurrentLocationOnClick}
        >
          <svg
            className="w-5 h-5 dark:text-white"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <path
              fillRule="evenodd"
              d="M11.906 1.994a8.002 8.002 0 0 1 8.09 8.421 7.996 7.996 0 0 1-1.297 3.957.996.996 0 0 1-.133.204l-.108.129c-.178.243-.37.477-.573.699l-5.112 6.224a1 1 0 0 1-1.545 0L5.982 15.26l-.002-.002a18.146 18.146 0 0 1-.309-.38l-.133-.163a.999.999 0 0 1-.13-.202 7.995 7.995 0 0 1 6.498-12.518ZM15 9.997a3 3 0 1 1-5.999 0 3 3 0 0 1 5.999 0Z"
              clipRule="evenodd"
            />
          </svg>
          <span>Find Nearby Locations</span>
        </button>
        <div className="flex max-w-[300px]">
          {!isGeolocationSupportedByThisBrowser && (
            <p className={buttonHelperTextStyles}>
              Geolocation not supported by this browser. Please use a different browser or device
              that supports geolocation.
            </p>
          )}
          {geolocationPermissionState === 'denied' && (
            <p className={buttonHelperTextStyles}>
              Location access is blocked. Enable it in your browser or device settings to use this
              feature.
            </p>
          )}
          {geolocationPermissionState === 'prompt' && (
            <p className={buttonHelperTextStyles}>
              Your location is only used to find nearby venues and is not stored, tracked, or shared
              with third parties.
            </p>
          )}
        </div>
        <OrDivider />
        <div className="max-w-sm">
          <ClearableFieldWrapper
            showClear={addressSearchInput.length > 0}
            onClear={() => {
              setAddressSearchInput('')
            }}
            label="Search address input"
          >
            <input
              id="clp_venueSearchInput"
              placeholder="Enter name or postcode..."
              onChange={(e) => {
                setAddressSearchInput(e.target.value)
                setShowNearbyLocationResultsRatherThanSearchInputResults(false)
                setShowNoResultsFoundMessage(false)
                setLocationReadError(null)
              }}
              value={addressSearchInput}
              type="text"
              className={[
                'appearance-none border border-gray-300 dark:border-slate-600 dark:bg-slate-900/60',
                'focus:border-sky-500 dark:focus:border-sky-400',
                'rounded w-full py-2 pl-3 pr-8',
                'text-gray-700 dark:text-slate-200 leading-tight focus:outline-none',
              ].join(' ')}
            />
          </ClearableFieldWrapper>
        </div>
      </div>

      {hasFetchSucceededAtLeastOnce && (
        <div className="flex justify-center gap-1 pt-3">
          <p className="text-xs text-gray-500 dark:text-slate-400">Can't find your location? </p>
          <button
            className="text-xs text-sky-500 dark:text-sky-400 hover:text-sky-400 dark:hover:text-sky-300"
            onClick={handleSubmitVenueOnClick}
          >
            Submit Your Venue
          </button>
        </div>
      )}

      {isFetching && (
        <div>
          <div
            role="status"
            className="pt-6 flex justify-center items-center gap-2"
          >
            <p className="text-slate-500 dark:text-slate-400 text-sm">Loading...</p>
            <LoadingSpinner size="small" />
          </div>
        </div>
      )}

      {Array.isArray(nearbyLocationsResults) &&
        nearbyLocationsResults.length === 0 &&
        showNearbyLocationResultsRatherThanSearchInputResults &&
        !isFetching &&
        !venueSearchFailure &&
        !locationReadError &&
        geolocationPermissionState !== 'denied' && (
          <div className={inlineToastContainerMainStyles}>
            <div className={inlineToastContainerMaxWidthStyles}>
              <InlineToast
                message="No venues were found within 50 km of your current location."
                title="No Nearby Locations Found"
                variant="info"
              />
            </div>
          </div>
        )}

      {showNoResultsFoundMessage && (
        <div className={inlineToastContainerMainStyles}>
          <div className={inlineToastContainerMaxWidthStyles}>
            <InlineToast
              message={`No venues found that match the following search query:`}
              title="No Results Found"
              variant="info"
            >
              <span
                className={[
                  'font-semibold break-all py-2 px-4',
                  'rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 w-full',
                ].join(' ')}
              >
                {addressSearchInput}
              </span>
            </InlineToast>
          </div>
        </div>
      )}

      {locationReadError && !isFetching && (
        <div className={inlineToastContainerMainStyles}>
          <div className={inlineToastContainerMaxWidthStyles}>
            <InlineToast
              message={locationReadErrorCopy[locationReadError].message}
              title={locationReadErrorCopy[locationReadError].title}
              variant="warning"
            />
          </div>
        </div>
      )}

      {venueSearchFailure && !isFetching && (
        <div className={inlineToastContainerMainStyles}>
          <div className={inlineToastContainerMaxWidthStyles}>
            <InlineToast
              message={venueSearchErrorCopy[venueSearchFailure].message}
              title={venueSearchErrorCopy[venueSearchFailure].title}
              variant={venueSearchErrorCopy[venueSearchFailure].variant}
            />
          </div>
        </div>
      )}

      <ul
        className="py-6 divide-y divide-slate-200 dark:divide-slate-700"
        role="listbox"
        hidden={toggleVenueSubmissionForm}
      >
        {renderedResultsList()}
      </ul>
    </>
  )
}

/* ************************************************************************** */
/* Helper functions */
/* ************************************************************************** */

function formatAddressDistanceForDisplay(distanceKm: number | string) {
  const distanceKmNum = castStringToNumber(distanceKm)
  if (distanceKmNum < 0.1) {
    return '<0.1'
  }
  const roundedToOneDecimal = Math.round(distanceKmNum * 10) / 10
  return roundedToOneDecimal.toString()
}

// The backend will include duplicated addresses in search results if multiple
// customers send product to the same bar/restaurant, which of course happens
// all the time. So it's critical for us to deduplicate addresses by addressId
// every time we display address results to the end user. The consequence of
// this is that the web app is essentially picking a random choice out of the
// several duplicated addresses to display to the user, meaning that the
// customerId of the chosen address is random. The backend team is OK with this.
// NOTE: "What about if two addresses, with different addressIds, have all the
// same address1, city, etc fields?" That's a data problem that we're not going
// to fix in the frontend (we could, but it would require up to hundreds of
// string comparisons every keystroke, which risks perceptible lag, especially
// on mobile devices).
function dedupeAddresses(addresses: Address[]): Address[] {
  return _.uniqBy(addresses, 'addressId')
}
